import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { playerAvatar, promotionAvatar } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Camera, Trophy, Swords, Users as UsersIcon, Shield, UserPlus, Mail, Users2, Crown, Flame } from "lucide-react";
import { ChampionCrown, useChampionId } from "../components/ChampionCrown";
import ReferralBlock from "../components/ReferralBlock";
import { CountUp, SectionHeading } from "../components/wrestler/WrestlerUI";
import { ProfileStat, AffilChip, RosterShowcaseCard } from "../components/profile/ProfileUI";
import { PageSparkles } from "../components/pvp/PvpUI";

// Which title divisions a wrestler can actually WIN (based on class).
function eligibleTitleDivisions(classId) {
  if (classId === "heavyweight" || classId === "middleweight" || classId === "cruiserweight") return [classId];
  if (classId === "tag_team" || classId === "tag_team_1") return ["tag_team"];
  if (classId === "wildcard") return ["heavyweight", "middleweight", "cruiserweight"];
  return [];
}
const DIVISION_LABEL_SHORT = { heavyweight: "HW", middleweight: "MW", cruiserweight: "CW", tag_team: "TAG" };
const DIVISION_LABEL_FULL = { heavyweight: "HEAVYWEIGHT", middleweight: "MIDDLEWEIGHT", cruiserweight: "CRUISERWEIGHT", tag_team: "TAG TEAM" };
const DIVISION_COLOR = { heavyweight: "#FFD700", middleweight: "#FFD700", cruiserweight: "#FFD700", tag_team: "#39FF14" };

function PageBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 -z-0 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: "url('/backgroundgraphics/pvpshowspage_background.webp')" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,5,26,0.55) 0%, rgba(10,5,26,0.92) 70%)" }} />
      <div className="wr-spot hidden md:block absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-15 bg-[#FF007F]" />
      <div className="wr-spot hidden md:block absolute top-1/3 -right-40 w-[560px] h-[560px] rounded-full blur-3xl opacity-10 bg-[#00F0FF]" style={{ animationDelay: "-2.5s" }} />
      <PageSparkles />
      <div className="wr-grain absolute inset-0" />
    </div>
  );
}



function Percentile({ value, testId }) {
  if (value == null) return null;
  return (
    <div className="relative mt-1.5 inline-flex items-center px-1.5 py-0.5 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/50 font-mono text-[10px] tracking-widest text-[#39FF14]"
         style={{ textShadow: "0 0 6px #39FF1466" }} data-testid={testId}>
      TOP {value}%
    </div>
  );
}

function PvpRecord({ profile }) {
  const w = profile.pvp_wins || 0;
  const l = profile.pvp_losses || 0;
  const rate = w + l > 0 ? Math.round((w / (w + l)) * 100) : 0;
  return (
    <>
      <div className="flex items-baseline gap-2 font-num leading-none">
        <span className="text-4xl" style={{ color: "#39FF14", textShadow: "0 0 10px #39FF1488" }} data-testid="stat-pvp-wins-value">{w}</span>
        <span className="text-xl text-[#695D8E]">/</span>
        <span className="text-4xl" style={{ color: "#FF007F", textShadow: "0 0 10px #FF007F88" }} data-testid="stat-pvp-losses-value">{l}</span>
      </div>
      <div className="flex items-center justify-between text-[9px] uppercase tracking-widest font-mono mt-1.5">
        <span className="text-[#39FF14]">Wins</span>
        <span className="font-num text-sm text-[#FFD700]" data-testid="stat-pvp-winrate">{rate}%</span>
        <span className="text-[#FF007F]">Losses</span>
      </div>
      <div className="mt-1 h-2 rounded-full overflow-hidden bg-[#1a0a2a] border border-[#39194D] flex">
        <div className="h-full" style={{ width: `${rate}%`, background: "linear-gradient(90deg,#39FF14,#9BFF7A)", boxShadow: "0 0 8px #39FF14AA" }} data-testid="stat-pvp-winrate-bar" />
        <div className="h-full" style={{ width: `${100 - rate}%`, background: "linear-gradient(90deg,#FF5FA8,#FF007F)", boxShadow: "0 0 8px #FF007FAA" }} data-testid="stat-pvp-lossrate-bar" />
      </div>
    </>
  );
}

export default function ProfilePage() {
  const { account_name: routeName } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const champId = useChampionId();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [myPromotion, setMyPromotion] = useState(null);
  const fileInputRef = useRef(null);
  const identityRef = useRef(null);
  const [avatarPx, setAvatarPx] = useState(null);

  // Target account name — if route has one, view that. Otherwise view own.
  const targetName = routeName || user?.account_name;
  const isSelf = !routeName || (user && user.account_name === routeName);

  const load = async () => {
    if (!targetName) return;
    setLoading(true);
    try {
      const [rp, rmp] = await Promise.all([
        api.get(`/players/${encodeURIComponent(targetName)}/profile`),
        api.get(`/me/promotion`).catch(() => ({ data: { promotion: null } })),
      ]);
      setProfile(rp.data);
      setMyPromotion(rmp.data?.promotion || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Player not found");
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetName]);

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("File must be under 3 MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/me/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      await load();
      toast.success("Avatar updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const savePromotion = async () => { /* deprecated — Promotion is now an entity page */ };

  const inviteToMyPromotion = async () => {
    if (!myPromotion || !profile?.account_name) return;
    try {
      await api.post(`/promotions/${myPromotion.promotion_id}/invite`, { account_name: profile.account_name });
      toast.success(`Invited ${profile.account_name} to ${myPromotion.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const [ttState, setTtState] = useState({ myTeam: null, targetTeam: null, targetHasTT: false });
  useEffect(() => {
    if (!profile) return;
    let cancel = false;
    (async () => {
      try {
        const [myR, targetR] = await Promise.all([
          isSelf ? Promise.resolve({ data: { team: null } }) : api.get("/tag-teams/mine").catch(() => ({ data: null })),
          api.get(`/tag-teams/mine`).catch(() => ({ data: null })),
        ]);
        // targetR is for viewer's own — reuse
        // Determine target's tag team wrestler existence & team
        const targetHasTT = (profile.wrestlers || []).some(w => (w.class_info?.id === "tag_team" || w.class_info?.id === "tag_team_1"));
        let targetTeam = null;
        if (targetHasTT && !isSelf) {
          // We'd need an endpoint to check — for now leverage that if targetHasTT and no team returned via mine
          // Simpler: send invite and let backend reject if teamed
        }
        if (!cancel) setTtState({ myTeam: myR.data?.team || null, targetTeam, targetHasTT });
      } catch {}
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [profile?.user_id]);

  const [ttInviteOpen, setTtInviteOpen] = useState(false);
  const [ttName, setTtName] = useState("");
  const sendTagTeamInvite = async () => {
    const name = ttName.trim();
    if (name.length < 3) return toast.error("Team name at least 3 chars");
    try {
      await api.post("/tag-teams/invite", { partner_account_name: profile.account_name, team_name: name });
      toast.success(`Tag Team invite sent to ${profile.account_name}!`);
      setTtInviteOpen(false); setTtName("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const el = identityRef.current;
    if (!el) return;
    const mq = window.matchMedia("(min-width: 1536px)");
    const sync = () => setAvatarPx(mq.matches ? Math.min(280, Math.max(224, Math.round(el.getBoundingClientRect().height))) : null);
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    mq.addEventListener("change", sync);
    sync();
    return () => { ro.disconnect(); mq.removeEventListener("change", sync); };
  }, [profile]);

  if (loading) return <div className="p-8 text-center font-heading neon-cyan">LOADING PROFILE...</div>;
  if (!profile) return <div className="p-8 text-center font-heading neon-pink">PLAYER NOT FOUND</div>;

  const iconBtn = "p-2 rounded-xl border-2 bg-[#0A051A]/70 backdrop-blur transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 active:scale-95";
  return (
    <div className="relative min-h-screen">
      <PageBackdrop />
      <div className="relative max-w-[1600px] mx-auto px-4 py-6" data-testid="profile-page">
        {/* Hero */}
        <div className="wr-rise mb-8">
        <div className="pf-hero-border wr-card relative rounded-3xl p-[2px]" style={{ "--wr-glow": "#FF007F" }}>
          <div className="relative rounded-[22px] overflow-hidden bg-[#110726]">
            <div className="pf-kenburns pointer-events-none absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/backgroundgraphics/Profile%20Page%20Banner.webp')" }} aria-hidden="true" />
            <div className="pf-sweep pointer-events-none absolute inset-y-0 -left-1/3 w-1/3" aria-hidden="true" />
            <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(120% 120% at 25% 40%, rgba(10,5,26,0.2) 0%, rgba(10,5,26,0.6) 55%, rgba(10,5,26,0.9) 100%)" }} aria-hidden="true" />
            <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,5,26,0.15) 0%, rgba(10,5,26,0.25) 45%, rgba(10,5,26,0.85) 100%)" }} aria-hidden="true" />
            <div className="wr-drift pointer-events-none absolute -bottom-24 left-1/3 w-72 h-72 rounded-full opacity-20 blur-3xl bg-[#FF007F]" aria-hidden="true" />

            <div className="relative z-10 p-5 sm:p-7 flex flex-col 2xl:flex-row items-center 2xl:items-start gap-6 2xl:gap-8">
              {/* Avatar with spinning neon ring */}
              <div className="relative shrink-0">
                <div className="relative rounded-[22px] p-[4px] overflow-hidden" style={{ boxShadow: "0 0 40px #FF007F66, 0 0 80px #9D00FF33" }}>
                  <div className="wr-frame-spin absolute -inset-[60%]" aria-hidden="true" />
                  <div
                    className={`relative w-[12.5rem] h-[12.5rem] sm:w-[14rem] sm:h-[14rem] rounded-[18px] overflow-hidden bg-[#0A051A] ${isSelf ? "cursor-pointer group" : ""}`}
                    style={avatarPx ? { width: avatarPx - 8, height: avatarPx - 8 } : undefined}
                    onClick={() => isSelf && fileInputRef.current?.click()}
                    data-testid="profile-avatar-wrap"
                  >
                    {profile.promotion && (
                      <img data-testid="profile-avatar-bg" src={promotionAvatar(profile.promotion.id, profile.promotion.avatar_updated_at)} alt=""
                           className="absolute inset-0 w-full h-full object-cover opacity-95" draggable={false} />
                    )}
                    <img data-testid="profile-avatar-fg" src={playerAvatar(profile.account_name, profile.player_avatar_updated_at)} alt={profile.account_name}
                         className="relative w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {profile.promotion && champId === profile.promotion.id && (
                      <ChampionCrown className="top-1 left-1/2 -translate-x-1/2 w-10 h-10" testId="profile-champion-crown" />
                    )}
                    <div data-testid="profile-avatar-level-banner"
                         className="absolute top-2 left-2 z-20 pointer-events-none flex items-center gap-1 px-2 py-0.5 rounded-lg border-2 border-[#00F0FF] bg-[#0A051A]/90 backdrop-blur whitespace-nowrap"
                         style={{ boxShadow: "0 0 12px #00F0FF88" }}>
                      <Crown size={12} strokeWidth={2.6} className="text-[#FFD700]" style={{ filter: "drop-shadow(0 0 4px #FFD700)" }} />
                      <span className="text-[8px] uppercase tracking-widest font-mono text-[#00F0FF]">LVL</span>
                      <span className="font-num text-lg leading-none text-[#00F0FF]" style={{ textShadow: "0 0 8px #00F0FF" }} data-testid="profile-avatar-level-value">
                        {(profile.combined_level || 0).toLocaleString()}
                      </span>
                    </div>
                    {isSelf && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera size={30} className="text-[#00F0FF] mb-1" />
                        <span className="text-[10px] uppercase tracking-widest font-mono text-white">{uploading ? "UPLOADING..." : "CHANGE"}</span>
                      </div>
                    )}
                  </div>
                </div>
                {isSelf && (
                  <input ref={fileInputRef} data-testid="profile-avatar-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                         onChange={(e) => uploadAvatar(e.target.files?.[0])} />
                )}
              </div>

              {/* Identity */}
              <div ref={identityRef} className="w-full 2xl:w-auto flex-1 min-w-0 2xl:self-start flex flex-col text-center 2xl:text-left" data-testid="profile-identity">
                <div className="flex items-center gap-2 justify-center 2xl:justify-start mb-1">
                  <div className="h-[2px] w-10 rounded-full" style={{ background: "linear-gradient(90deg, #FF007F00, #FF007F)" }} aria-hidden="true" />
                  <span className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#C9BDF0]">{isSelf ? "Your Profile" : "Promoter Profile"}</span>
                  <div className="h-[2px] w-10 rounded-full" style={{ background: "linear-gradient(90deg, #00F0FF, #00F0FF00)" }} aria-hidden="true" />
                </div>
                <h1 className="font-graffiti text-4xl sm:text-5xl lg:text-6xl leading-none mb-6 flex flex-wrap items-center gap-3 justify-center 2xl:justify-start" data-testid="profile-name">
                  <span className="pf-flicker break-words min-w-0 max-w-full" style={{ color: "#FF007F", textShadow: "0 0 18px #FF007FAA, 0 0 40px #FF007F55, 3px 3px 0 #000" }}>{profile.account_name}</span>
                  {!isSelf && (
                    <button data-testid="profile-dm-btn" onClick={() => nav(`/inbox/${encodeURIComponent(profile.account_name)}`)}
                            title={`Send a message to ${profile.account_name}`} aria-label={`Send a message to ${profile.account_name}`}
                            className={`${iconBtn} border-[#FFE600]/70 text-[#FFE600] hover:bg-[#FFE600]/15 hover:shadow-[0_0_14px_#FFE60088]`}>
                      <Mail size={20} />
                    </button>
                  )}
                  {!isSelf && ttState.targetHasTT && !ttState.myTeam && (
                    <button data-testid="profile-tag-team-invite-btn" onClick={() => setTtInviteOpen(true)} title="Invite this player's Tag Team wrestler"
                            className={`${iconBtn} border-[#39FF14]/70 text-[#39FF14] hover:bg-[#39FF14]/15 hover:shadow-[0_0_14px_#39FF1488]`}>
                      <Users2 size={20} />
                    </button>
                  )}
                </h1>

                <div className="flex flex-wrap items-end gap-x-8 gap-y-4 justify-center 2xl:justify-start">
                  {profile.tag_team && (
                    <div data-testid="profile-tag-team-block">
                      <AffilChip label="Tag Team" color="#39FF14" testId="profile-tag-team-badge" title={`View ${profile.tag_team.name}`}
                                 onClick={() => nav(`/tag-team/${profile.tag_team.team_id}`)}>
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#39FF14]/15 border border-[#39FF14]/60"><Users2 size={16} /></span>
                        <span className="tracking-wide">{profile.tag_team.name}</span>
                      </AffilChip>
                    </div>
                  )}
                  {profile.promotion ? (
                    <AffilChip label="Promotion" color="#00F0FF" testId="profile-promo-badge" title={`View ${profile.promotion.name}`}
                               onClick={() => nav(`/promotion/${encodeURIComponent(profile.promotion.id)}`)}>
                      <img src={promotionAvatar(profile.promotion.id, profile.promotion.avatar_updated_at)} alt={profile.promotion.name}
                           className="w-8 h-8 rounded-lg object-cover border border-[#00F0FF]/60" />
                      <span className="tracking-wide">{profile.promotion.name}</span>
                      {profile.promotion.is_leader && <Shield size={14} className="text-[#FFD700]" style={{ filter: "drop-shadow(0 0 4px #FFD700)" }} />}
                    </AffilChip>
                  ) : (
                    <div className="flex flex-wrap items-end gap-2">
                      <AffilChip label="Promotion" color="#695D8E" testId="profile-promo-display">
                        <span className="px-1 py-1.5 text-[#695D8E]">— Independent —</span>
                      </AffilChip>
                      {isSelf ? (
                        <button data-testid="profile-promo-found" onClick={() => nav("/promotion")} className="btn-cyan text-[10px] px-3 py-2 flex items-center gap-1">
                          <Shield size={12} /> Found / Join
                        </button>
                      ) : (myPromotion && myPromotion.leader_id === user?.user_id) ? (
                        <button data-testid="profile-invite-to-promotion" onClick={inviteToMyPromotion} className="btn-neon text-[10px] px-3 py-2 flex items-center gap-1" title={`Invite to ${myPromotion.name}`}>
                          <UserPlus size={12} /> Invite to {myPromotion.name}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 w-full 2xl:w-[720px] shrink-0 2xl:self-start">
                <ProfileStat testId="stat-combined-level" label="Combined Level" color="#00F0FF" icon={Crown} delay={120}
                             extra={<Percentile value={profile.combined_level_percentile} testId="stat-level-percentile" />}>
                  <CountUp value={profile.combined_level || 0} className="font-num text-4xl leading-none" style={{ color: "#00F0FF", textShadow: "0 0 12px #00F0FF88" }} testId="stat-combined-level-value" />
                </ProfileStat>
                <ProfileStat testId="stat-hof-points" label="HOF Points" color="#B44DFF" icon={Trophy} delay={180}
                             extra={<Percentile value={profile.hof_points_percentile} testId="stat-hof-percentile" />}>
                  <CountUp value={profile.total_hof_points || 0} className="font-num text-4xl leading-none" style={{ color: "#B44DFF", textShadow: "0 0 12px #B44DFF88" }} />
                </ProfileStat>
                <ProfileStat testId="stat-pvp-record" label="PvP Record" color="#FFD700" icon={Swords} delay={240}>
                  <PvpRecord profile={profile} />
                </ProfileStat>
                <ProfileStat testId="stat-win-streak" label="Win Streak" color="#FF4500" icon={Flame} delay={300}>
                  <div className="flex items-end gap-2">
                    <CountUp value={profile.win_streak || 0} className="font-num text-4xl leading-none" style={{ color: "#FF4500", textShadow: "0 0 12px #FF450088" }} />
                    {(profile.win_streak || 0) >= 3 && <Flame size={22} className="mb-1 text-[#FF9500] animate-pulse" style={{ filter: "drop-shadow(0 0 6px #FF4500)" }} />}
                  </div>
                </ProfileStat>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Roster showcase */}
        <SectionHeading icon={UsersIcon} color="#00F0FF" className="mb-4" testId="profile-roster-heading"
          action={isSelf && (
            <button onClick={() => nav("/roster")} data-testid="profile-manage-roster"
                    className="ml-auto text-[10px] uppercase tracking-widest font-mono text-[#A89BCC] hover:text-[#00F0FF] transition-colors">
              Manage roster →
            </button>
          )}>
          Roster <span className="font-num text-[#FF007F]" style={{ textShadow: "0 0 10px #FF007F99" }}>({profile.wrestlers.length})</span>
        </SectionHeading>

        {profile.wrestlers.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#2D1C52] bg-[#0A051A]/70 p-10 text-center text-[#A89BCC] font-graffiti text-xl" data-testid="profile-empty-roster">
            No wrestlers signed yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {profile.wrestlers.map((w, i) => (
              <RosterShowcaseCard key={w.wrestler_id} w={w} delay={200 + i * 80} divisions={eligibleTitleDivisions(w.class_id || w.class_info?.id)}
                                  labels={DIVISION_LABEL_FULL} short={DIVISION_LABEL_SHORT} colors={DIVISION_COLOR} />
            ))}
          </div>
        )}

        {ttInviteOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="tt-invite-modal">
            <div className="card-surface p-5 max-w-sm w-full" style={{ borderColor: "#39FF14AA" }}>
              <h3 className="font-heading text-xl neon-green uppercase tracking-widest mb-3">Form Tag Team</h3>
              <p className="text-xs font-mono text-[#A89BCC] mb-3">
                Invite <span className="neon-cyan">{profile.account_name}</span>'s Tag Team wrestler to join yours.
              </p>
              <input data-testid="tt-invite-name-input" value={ttName} onChange={(e) => setTtName(e.target.value)} placeholder="Tag Team Name" maxLength={24}
                     className="w-full px-3 py-2 rounded-md bg-[#0A051A] border border-[#39FF14]/60 focus:border-[#39FF14] outline-none font-heading text-sm uppercase mb-3" />
              <div className="flex gap-2">
                <button onClick={() => setTtInviteOpen(false)} className="flex-1 px-3 py-2 rounded-md border border-[#39194D] text-[#A89BCC] hover:text-white text-xs font-heading uppercase">Cancel</button>
                <button data-testid="tt-invite-send" onClick={sendTagTeamInvite} className="flex-1 btn-neon text-xs py-2">Send Invite</button>
              </div>
            </div>
          </div>
        )}

        {/* Referral link — only visible on your own profile */}
        {isSelf && <div className="mt-8"><ReferralBlock /></div>}
      </div>
    </div>
  );
}
